import Api from '@/api/affiliate-api'
import { createLogger } from '@/common/log'
const logger = createLogger("AWIN", "", false)

const timestamp = new Date().getTime()
const awinScript = awinId => `https://www.dwin1.com/${awinId}.js?v=${timestamp}`
let awinObject = {}

const loadScript = (url, defer) => {
  return new Promise((resolve, reject) => {
    const head = document.head || document.getElementsByTagName('head')[0]
    const script = document.createElement('script')
    script.async = true
    script.src = url
    script.id = 'awin-js'
    script.charset = 'utf-8'
    if (defer) {
      script.setAttribute('defer', 'defer')
    }
    head.appendChild(script)
    script.onload = resolve
    script.onerror = reject
  })
}

const rerunScript = () => {
  awinObject.internal.Tracking.run()
}

const sendUsingImgFallback = url => {
  let awinFallbackImg = document.createElement('img')
  awinFallbackImg.id = 'awinFallbackImg'
  awinFallbackImg.border = 0
  awinFallbackImg.height = 0
  awinFallbackImg.width = 0
  awinFallbackImg.style = 'display:none'
  awinFallbackImg.src = url
  awinFallbackImg.onload = () => {
    //remove it after it has been loaded
    setTimeout(() => {
      awinFallbackImg.remove()
    }, 500)
  }
  document.body.appendChild(awinFallbackImg)
}

const mapProducts = function getOrderProducts(products) {
  if(!products) {
    return []
  }
  if (products.length > 0 && products[0].price) {
    return products.map(product => {
      return {
        identifier: `${product.id}`,
        amount: `${product.price.total}`,
        currency: `${product.price.currency}`,
        quantity: '1',
      }
    })
  }
  return products.map(product => {
    return {
      identifier: `${product.id}`,
      amount: `${product.unitPrice}`,
      currency: `${product.currency}`,
      quantity: `${product.quantity}`,
    }
  })
}

const knownVars = [
  'pagetype',
  'products',
  'language',
  'transaction',
  'total_amount',
  'total_currency',
  'search_query',
  'identifier',
  'category',
  'description',
  'amount',
  'price',
  'image1',
  'url',
]

const redeclare = vars => {
  for (let known of knownVars) {
    window['awin_' + known] = undefined
  }
  for (let v in vars) {
    window['awin_' + v] = vars[v]
  }
}

const awinOperations = (awin, id) => {
  return {
    setConversionTag: async (cartId, token) => {
      // try {
      //according to docu https://wiki.awin.com/index.php/Advertiser_Tracking_Guide/Standard_Implementation
      // see Example #2, conversion after mastertag

      const summaryData = await Api.affiliateSummary(cartId, token)
      let parts = 'DEFAULT'
      if (summaryData.usedReduction) {
        parts = 'VOUCHER'
      } else if (summaryData.customer == 0) {
        //customer state 0 = new customer
        parts = 'NEW'
      } else {
        //customer state 1 = returning customer
        parts = 'EXISTING'
      }

      awin.Tracking.Sale = {}
      awin.Tracking.Sale.amount = summaryData.total.toFixed(2)
      awin.Tracking.Sale.channel = 'aw'
      awin.Tracking.Sale.orderRef = `${summaryData.id}`
      awin.Tracking.Sale.parts = parts + ':' + summaryData.total.toFixed(2)
      awin.Tracking.Sale.currency = summaryData.currency
      awin.Tracking.Sale.test = '0'

      awin.Tracking.run()

      sendUsingImgFallback(
        `https://www.awin1.com/sread.img?tt=ns&tv=2&merchant=${id}&amount=${summaryData.total}&ch=aw&parts=${parts}:${summaryData.total}&ref=${summaryData.id}&cr=${summaryData.currency}&testmode=0`,
      )
      // } catch {}
    },
    setConfirmation: (locale, total, cartId, currency, products) => {
      const zxParams = {
        pagetype: 'checkout',
        products: mapProducts(products),
        language: `${locale}`,
        transaction: `${cartId}`,
        total_amount: `${total}`,
        total_currency: `${currency}`,
      }
      redeclare(zxParams)
      rerunScript()
    },
    setCart: (locale, products) => {
      const zxParams = {
        pagetype: 'basket',
        products: mapProducts(products),
        language: `${locale}`,
      }
      redeclare(zxParams)
      rerunScript()
    },
    setHome: locale => {
      const zxParams = {
        pagetype: 'home',
        language: `${locale}`,
      }
      redeclare(zxParams)
      rerunScript()
    },
    setSearch: (products, query, locale) => {
      const zxParams = {
        pagetype: 'search',
        search_query: `${query}`,
        products: mapProducts(products),
        language: `${locale}`,
      }
      redeclare(zxParams)
      rerunScript()
    },
    setOffer: (template, url, locale) => {
      //get variant with lowest price
      let minPriceVariant = template.variants.reduce(function(prev, curr) {
        return prev.price.total < curr.price.total ? prev : curr
      })

      const zxParams = {
        pagetype: 'product',
        identifier: `${template.id}`,
        fn: `${template.subtitle}`,
        description: `${template.title}`,
        category: 'Urlaubsgutscheine',
        price: `${minPriceVariant.price.currency} ${minPriceVariant.price.total}`,
        amount: `${minPriceVariant.price.total}`,
        currency: `${minPriceVariant.price.currency}`,
        url: `${url}`,
        image1: `https://assets.urlaubsgutschein.kaufen/${template.image}`,
        language: `${locale}`,
      }

      redeclare(zxParams)
      rerunScript()
      //awin.Tracking.run()
    },
  }
}

export default function install(Vue, options = {}) {
  logger.debug('installing awin')
  let script = loadScript(awinScript(options.awinId), false)
  script.then(
    () => {
      logger.debug('awin installed')
      const windowObject = AWIN || window.AWIN // eslint-disable-line
      if (windowObject) {
        logger.debug('awin initialized', {id: options.awinId})
        awinObject = {
          ...awinOperations(windowObject, options.awinId),
        }
        awinObject.internal = windowObject
      }
      Vue.prototype.$awin = awinObject
    },
    err => {
      logger.debug('awin NOT installed', err)
    },
  )
}
